import { useState, useEffect } from 'react'
import { useBoardroomData } from '../../hooks/useBoardroomData'
import { LoadingSpinner } from '../../components/Common/LoadingSpinner'
import { TrendingUp, AlertCircle, BarChart3, Zap, Users, Award } from 'lucide-react'

const fmtR = (val) => {
  const n = Number(val) || 0
  return `R${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// ── Real-data Performance Panel (replaces fake AI Recommendations) ─────────
function PerformancePanel({ data, isDarkMode }) {
  const bgColor = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
  const textColor = isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'
  const subColor = isDarkMode ? 'text-slate-500' : 'text-gray-400'

  const growth = data.growthVsMonth
  const topProduct = data.commissionByProduct[0]

  const items = [
    {
      icon: growth >= 0 ? '📈' : '📉',
      title: `${Math.abs(growth).toFixed(1)}% vs Last Month`,
      text: growth >= 0
        ? `Commission is up ${fmtR(data.totalCommission - data.previousTotalCommission)} from last month`
        : `Commission is down ${fmtR(data.previousTotalCommission - data.totalCommission)} from last month`,
      style: growth >= 0
        ? isDarkMode ? 'bg-green-500/20 border-green-500/30 text-green-200' : 'bg-green-50 border-green-200 text-green-700'
        : isDarkMode ? 'bg-red-500/20 border-red-500/30 text-red-200' : 'bg-red-50 border-red-200 text-red-700',
    },
    {
      icon: '🏆',
      title: data.topBroker.name !== '—' ? data.topBroker.name : 'No broker data',
      text: data.topBroker.commission > 0
        ? `Leading at ${fmtR(data.topBroker.commission)} this month`
        : 'No transactions this month',
      style: isDarkMode ? 'bg-yellow-500/20 border-yellow-500/30 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-700',
    },
    topProduct && {
      icon: '🎯',
      title: topProduct.name.length > 22 ? topProduct.name.slice(0, 22) + '…' : topProduct.name,
      text: `Top product · ${fmtR(topProduct.amount)} commission`,
      style: isDarkMode ? 'bg-blue-500/20 border-blue-500/30 text-blue-200' : 'bg-blue-50 border-blue-200 text-blue-700',
    },
  ].filter(Boolean)

  return (
    <div className={`${bgColor} rounded-xl p-6 border shadow-md h-full flex flex-col`}>
      <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${textColor}`}>
        <Zap className="w-5 h-5 text-sfp-orange" />
        This Month
      </h3>

      <div className="space-y-3 flex-1">
        {items.map((item, i) => (
          <div key={i} className={`rounded-lg p-3 border ${item.style}`}>
            <div className="flex items-start gap-2">
              <span className="text-lg leading-none mt-0.5">{item.icon}</span>
              <div>
                <p className="font-semibold text-sm">{item.title}</p>
                <p className="text-xs mt-0.5 opacity-90">{item.text}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={`mt-4 pt-3 border-t text-xs ${isDarkMode ? 'border-slate-700' : 'border-gray-200'} ${subColor}`}>
        {data.activeBrokers} active broker{data.activeBrokers !== 1 ? 's' : ''}
        {' · '}
        {data.clientsServed} client{data.clientsServed !== 1 ? 's' : ''} served
      </div>
    </div>
  )
}

// ── Interactive SVG Pie Chart ──────────────────────────────────────────────
function InteractivePieChart({ data, isDarkMode }) {
  const [hoveredSlice, setHoveredSlice] = useState(null)
  const total = data.reduce((sum, item) => sum + item.amount, 0)
  const colors = ['#F5A623', '#FF9500', '#FF7E3C', '#FF6B2C', '#FF5A1C', '#E04B0F']

  const slices = data.reduce((acc, item, idx) => {
    const currentAngle = idx === 0 ? -90 : acc[idx - 1].endAngle
    const pct = (item.amount / total) * 100
    const sliceAngle = (pct / 100) * 360
    const startRad = (currentAngle * Math.PI) / 180
    const endRad = ((currentAngle + sliceAngle) * Math.PI) / 180
    const r = 80
    const path = `M 100 100 L ${100 + r * Math.cos(startRad)} ${100 + r * Math.sin(startRad)} A ${r} ${r} 0 ${sliceAngle > 180 ? 1 : 0} 1 ${100 + r * Math.cos(endRad)} ${100 + r * Math.sin(endRad)} Z`
    acc.push({ ...item, path, pct, color: colors[idx % colors.length], endAngle: currentAngle + sliceAngle })
    return acc
  }, [])

  const bg = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
  const text = isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'

  return (
    <div className={`${bg} rounded-xl p-6 border shadow-md`}>
      <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${text}`}>
        <BarChart3 className="w-5 h-5 text-sfp-orange" />
        Commission Mix
      </h3>
      <div className="flex flex-col items-center">
        <svg width="200" height="200" viewBox="0 0 200 200" className="mb-3">
          {slices.map((s, i) => (
            <g key={i} onMouseEnter={() => setHoveredSlice(i)} onMouseLeave={() => setHoveredSlice(null)}>
              <path
                d={s.path}
                fill={s.color}
                opacity={hoveredSlice === null || hoveredSlice === i ? 1 : 0.4}
                className="transition-opacity cursor-pointer"
                stroke={isDarkMode ? '#1e293b' : '#fff'}
                strokeWidth="2"
              />
            </g>
          ))}
        </svg>
        <div className="w-full space-y-1.5">
          {slices.map((s, i) => (
            <div
              key={i}
              onMouseEnter={() => setHoveredSlice(i)}
              onMouseLeave={() => setHoveredSlice(null)}
              className="flex items-center justify-between cursor-pointer transition opacity-80 hover:opacity-100"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                <span className={`text-xs truncate ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{s.name}</span>
              </div>
              <div className="text-right flex-shrink-0 ml-2">
                <span className="text-xs font-bold text-sfp-orange">{s.pct.toFixed(0)}%</span>
                <span className={`text-xs ml-1.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{fmtR(s.amount)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Forecast Card — real days + real trend bars ────────────────────────────
function ForecastCard({ data, isDarkMode }) {
  const now = new Date()
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
  const daysElapsed = Math.max(now.getDate(), 1)
  const daysRemaining = daysInMonth - daysElapsed

  const current = data.totalCommission

  // Simple run-rate projection: daily avg × remaining days
  const dailyAvg = current / daysElapsed
  const projected = current + dailyAvg * daysRemaining
  const potentialGain = current > 0 ? ((projected - current) / current) * 100 : 0

  // Trend bars: [prevPrev, prev, this, projected] normalised
  const trendValues = [...(data.monthlyTotals || [0, 0, current]), projected]
  const maxVal = Math.max(...trendValues, 1)

  const bg = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
  const text = isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'
  const label = isDarkMode ? 'text-slate-400' : 'text-gray-600'

  return (
    <div className={`${bg} rounded-xl p-6 border shadow-md`}>
      <h3 className={`font-bold text-lg mb-6 flex items-center gap-2 ${text}`}>
        <TrendingUp className="w-5 h-5 text-sfp-orange" />
        Month-End Forecast
      </h3>

      <div className="space-y-4">
        <div>
          <p className={`text-xs uppercase tracking-wide ${label}`}>
            Current ({daysElapsed} of {daysInMonth} days)
          </p>
          <p className="text-2xl font-bold text-sfp-orange mt-1">{fmtR(current)}</p>
        </div>

        <div>
          <p className={`text-xs uppercase tracking-wide ${label}`}>Projected Month-End</p>
          <p className={`text-3xl font-bold mt-1 ${text}`}>{fmtR(projected)}</p>
          {potentialGain > 0 && (
            <p className="text-sm text-green-500 mt-1 flex items-center gap-1">
              <Zap size={14} />
              {potentialGain.toFixed(1)}% potential gain
            </p>
          )}
        </div>

        <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'} pt-4`}>
          <p className={`text-xs uppercase tracking-wide mb-3 ${label}`}>3-Month Trend + Forecast</p>
          <div className="flex items-end gap-1.5 h-14">
            {trendValues.map((val, idx) => (
              <div
                key={idx}
                title={idx === 3 ? `Projected: ${fmtR(val)}` : fmtR(val)}
                className={`flex-1 rounded-t transition-all cursor-pointer ${
                  idx === 3
                    ? 'bg-gradient-to-t from-orange-300/60 to-orange-400/60 border border-dashed border-orange-400'
                    : 'bg-gradient-to-t from-sfp-orange to-orange-600'
                }`}
                style={{ height: `${Math.max((val / maxVal) * 100, 4)}%` }}
              />
            ))}
          </div>
          <div className={`flex justify-between text-xs mt-1 ${label}`}>
            <span>-2mo</span>
            <span>-1mo</span>
            <span>Now</span>
            <span className="text-orange-400">Est.</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Smooth animated counter ────────────────────────────────────────────────
function SmoothCounter({ value, format = 'currency' }) {
  const [display, setDisplay] = useState(value * 0.6)

  useEffect(() => {
    const diff = value - display
    const step = diff / 20
    const interval = setInterval(() => {
      setDisplay(prev => {
        const next = prev + step
        if (Math.abs(next - value) < 1) { clearInterval(interval); return value }
        return next
      })
    }, 30)
    return () => clearInterval(interval)
  }, [value])

  if (format === 'count') return <span>{Math.round(display).toLocaleString('en-ZA')}</span>
  return <span>{fmtR(display)}</span>
}

// ── Interactive KPI tile ───────────────────────────────────────────────────
function InteractiveKPI({ label, value, previous, icon: Icon, isDarkMode, format = 'currency' }) {
  const trend = value >= previous ? 1 : -1
  const trendPct = previous > 0 ? Math.abs(((value - previous) / previous) * 100).toFixed(1) : '0.0'

  const bg = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
  const text = isDarkMode ? 'text-white' : 'text-sfp-darkgray'
  const sub = isDarkMode ? 'text-slate-400' : 'text-gray-600'

  return (
    <div className={`${bg} rounded-xl p-6 border shadow-md cursor-pointer transition-all hover:border-sfp-orange hover:shadow-lg group hover:scale-105`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm font-semibold uppercase tracking-wider ${sub}`}>{label}</p>
          <p className={`text-3xl font-bold mt-3 font-mono group-hover:text-sfp-orange transition ${text}`}>
            <SmoothCounter value={typeof value === 'number' ? value : parseInt(value) || 0} format={format} />
          </p>
          <p className={`text-sm font-semibold mt-2 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend > 0 ? '▲' : '▼'} {trendPct}% vs prev month
          </p>
        </div>
        <Icon className="w-10 h-10 text-sfp-orange opacity-70 group-hover:opacity-100 transition" />
      </div>
    </div>
  )
}

// ── Live clock header ──────────────────────────────────────────────────────
function Header({ isDarkMode, secondsUntilRefresh, selectedMonth, onMonthChange }) {
  const [time, setTime] = useState(new Date())

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const bg = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
  const text = isDarkMode ? 'text-white' : 'text-sfp-darkgray'
  const sub = isDarkMode ? 'text-slate-400' : 'text-gray-600'
  const inputCls = isDarkMode
    ? 'bg-slate-700 border-slate-600 text-slate-200 focus:border-sfp-orange'
    : 'bg-white border-gray-300 text-sfp-darkgray focus:border-sfp-orange'

  const now = new Date()
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const isCurrentMonth = selectedMonth === currentMonth

  // Month label for subtitle
  const [selYear, selMon] = selectedMonth.split('-').map(Number)
  const monthLabel = new Date(selYear, selMon - 1, 1)
    .toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })

  return (
    <div className={`${bg} border-b px-8 py-5 flex justify-between items-center shadow-sm flex-wrap gap-4`}>
      <div>
        <h1 className={`text-4xl font-bold ${text}`}>Commission Dashboard</h1>
        <p className={`text-sm mt-1 ${sub}`}>
          {monthLabel}{isCurrentMonth ? ' · Live Data' : ' · Historical Data'}
        </p>
      </div>

      {/* Month picker */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <label className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>Month</label>
          <input
            type="month"
            value={selectedMonth}
            max={currentMonth}
            onChange={e => onMonthChange(e.target.value)}
            className={`border rounded px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-sfp-orange ${inputCls}`}
          />
        </div>
        {!isCurrentMonth && (
          <button
            onClick={() => onMonthChange(currentMonth)}
            className="text-xs px-3 py-1.5 rounded bg-sfp-orange/10 text-sfp-orange hover:bg-sfp-orange/20 font-semibold transition"
          >
            Current
          </button>
        )}
      </div>

      <div className="text-right">
        <p className="text-sfp-orange font-bold text-2xl">{time.toLocaleTimeString('en-ZA')}</p>
        {isCurrentMonth
          ? <p className={`text-xs ${sub}`}>Auto-refresh in {secondsUntilRefresh}s</p>
          : <p className={`text-xs ${sub}`}>Historical view</p>
        }
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────
export function BoardroomAI_Option2({ isDarkMode = false }) {
  const now = new Date()
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const [selectedMonth, setSelectedMonth] = useState(currentMonth)

  const { data, loading, secondsUntilRefresh } = useBoardroomData(selectedMonth)

  const bg = isDarkMode ? 'bg-slate-900' : 'bg-sfp-lightgray'

  if (loading || !data) {
    return <LoadingSpinner />
  }

  return (
    <div className={`${bg} min-h-screen transition-colors duration-300`}>
      <Header
        isDarkMode={isDarkMode}
        secondsUntilRefresh={secondsUntilRefresh}
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
      />

      <div className="p-6 space-y-8 max-w-7xl mx-auto">

        {/* ROW 1: KPI tiles + Performance panel + Pie chart */}
        <div className="grid grid-cols-4 gap-6">

          {/* KPI tiles — 2 cols */}
          <div className="col-span-2 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <InteractiveKPI
                label="Total Commission"
                value={data.totalCommission}
                previous={data.previousTotalCommission}
                icon={TrendingUp}
                isDarkMode={isDarkMode}
              />
              <InteractiveKPI
                label="Top Broker"
                value={data.topBroker.commission}
                previous={data.topBroker.previousCommission}
                icon={Award}
                isDarkMode={isDarkMode}
              />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <InteractiveKPI
                label="Avg per Policy"
                value={data.avgPerPolicy}
                previous={data.previousAvgPerPolicy}
                icon={BarChart3}
                isDarkMode={isDarkMode}
              />
              <InteractiveKPI
                label="Policies Written"
                value={data.policiesWritten}
                previous={data.previousPolicies}
                icon={BarChart3}
                isDarkMode={isDarkMode}
                format="count"
              />
            </div>
          </div>

          {/* Performance panel — 1 col */}
          <div className="col-span-1">
            <PerformancePanel data={data} isDarkMode={isDarkMode} />
          </div>

          {/* Pie chart — 1 col */}
          <div className="col-span-1">
            {data.commissionByInsurer.length > 0 ? (
              <InteractivePieChart data={data.commissionByInsurer} isDarkMode={isDarkMode} />
            ) : (
              <div className={`${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'} rounded-xl p-6 border shadow-md flex items-center justify-center`}>
                <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>No product data this month</p>
              </div>
            )}
          </div>
        </div>

        {/* ROW 2: Forecast + By Product bars */}
        <div className="grid grid-cols-2 gap-6">
          <ForecastCard data={data} isDarkMode={isDarkMode} />

          {/* Commission by product — horizontal bars */}
          <div className={`${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'} rounded-xl p-6 border shadow-md`}>
            <h2 className={`font-bold text-lg mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-sfp-darkgray'}`}>
              <BarChart3 className="w-5 h-5 text-sfp-orange" />
              By Product
            </h2>
            {data.commissionByProduct.length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>No product data</p>
            ) : (
              <div className="space-y-4">
                {data.commissionByProduct.slice(0, 5).map((item, idx) => {
                  const maxVal = data.commissionByProduct[0].amount
                  const pct = (item.amount / maxVal) * 100
                  return (
                    <div key={idx} className="group cursor-pointer hover:opacity-100 opacity-80 transition">
                      <div className="flex justify-between items-center mb-1.5">
                        <span className={`font-semibold text-sm truncate ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>
                          {item.name}
                        </span>
                        <span className="text-sfp-orange font-bold font-mono text-sm ml-2 flex-shrink-0">
                          <SmoothCounter value={item.amount} />
                        </span>
                      </div>
                      <div className={`rounded-full h-2.5 overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                        <div
                          className="bg-gradient-to-r from-sfp-orange to-orange-600 h-full transition-all duration-700"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* ROW 3: Broker Leaderboard */}
        <div className={`${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'} border rounded-xl overflow-hidden shadow-md`}>
          <div className={`p-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
            <h2 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-sfp-darkgray'}`}>
              🏆 Top Brokers — {new Date(data.selectedMonth + '-01').toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
            </h2>
          </div>
          {data.topBrokers.length === 0 ? (
            <div className={`p-8 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              No broker data for this month
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className={`${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'} border-b`}>
                  {['Rank', 'Broker', 'Commission', 'vs Last Month', 'Policies'].map(h => (
                    <th key={h} className={`px-6 py-4 text-left font-semibold text-xs uppercase tracking-wide ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.topBrokers.map((broker, idx) => {
                  const trendPct = broker.prevCommission > 0
                    ? ((broker.commission - broker.prevCommission) / broker.prevCommission) * 100
                    : null
                  return (
                    <tr
                      key={idx}
                      className={`border-b ${isDarkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'} transition cursor-pointer group`}
                    >
                      <td className="px-6 py-4 text-sfp-orange font-bold text-lg">
                        {broker.rank === 1 ? '🥇' : broker.rank === 2 ? '🥈' : broker.rank === 3 ? '🥉' : `#${broker.rank}`}
                      </td>
                      <td className={`px-6 py-4 font-semibold group-hover:text-sfp-orange transition ${isDarkMode ? 'text-white' : 'text-sfp-darkgray'}`}>
                        {broker.name}
                      </td>
                      <td className="px-6 py-4 text-sfp-orange font-bold font-mono">
                        <SmoothCounter value={broker.commission} />
                      </td>
                      <td className="px-6 py-4">
                        {trendPct !== null ? (
                          <span className={`text-sm font-semibold ${trendPct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {trendPct >= 0 ? '▲' : '▼'} {Math.abs(trendPct).toFixed(1)}%
                          </span>
                        ) : (
                          <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>New this month</span>
                        )}
                      </td>
                      <td className={`px-6 py-4 font-mono ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                        {broker.policies}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ROW 4: Bottom KPI strip */}
        <div className="grid grid-cols-4 gap-6 pb-8">
          <InteractiveKPI
            label="Active Brokers"
            value={data.activeBrokers}
            previous={data.previousActiveBrokers}
            icon={Users}
            isDarkMode={isDarkMode}
            format="count"
          />
          <InteractiveKPI
            label="Clients Served"
            value={data.clientsServed}
            previous={data.previousClientsServed}
            icon={Users}
            isDarkMode={isDarkMode}
            format="count"
          />
          <InteractiveKPI
            label="Prev Month Total"
            value={data.previousTotalCommission}
            previous={data.prevPrevTotal}
            icon={TrendingUp}
            isDarkMode={isDarkMode}
          />
          <InteractiveKPI
            label="Policies Written"
            value={data.policiesWritten}
            previous={data.previousPolicies}
            icon={AlertCircle}
            isDarkMode={isDarkMode}
            format="count"
          />
        </div>
      </div>
    </div>
  )
}
